/* ==========================================================================
   lp-nova-chat.js — behaviour for snippets/lp-nova-chat.liquid
   No dependencies. No framework. Safe to load with `defer`.

   Owns: open/close, a scripted intro sequence (plays once on first open,
   before any chips show), quick-reply chips, canned answers, a real
   AI reply for anything not covered by a chip (streamed), a real
   consolidated purchase widget -- flavor, one-click upsell, straight to
   cart, no further chat turns needed -- triggered either by a curated
   FAQ entry / keyword match, OR (2026-08-20) by the AI itself calling
   the present_purchase_widget tool when it judges the moment is right.
   WIDGET_HANDLERS maps tool name -> renderer; adding a new AI-triggered
   widget later is a backend/src/tools.js entry + a case here, nothing
   else changes. Add-to-cart routes through this theme's own
   `theme:cart:add` event, not a hand-rolled cart fetch. Typing
   indicator, thread auto-scroll.

   Does NOT own: any colour, size or timing value that belongs in lp-nova.css.
   Content (intro, FAQ, chat endpoint) comes from the JSON block the
   snippet renders. Does NOT own the AI backend itself -- that's
   backend/ in THIS branch (nova-chat-concierge, its own Cloudflare
   Worker, siloed 2026-08-20 from ai-chat-pdp's nova-chat-agent per
   Alex so neither can break the other) -- reused via `chat_endpoint`,
   not duplicated in this file.

   NOTE (2026-08-18): the teaser (proactive pop-up card, timed entry,
   dismiss button) was removed entirely per Alex, after panel/hidden CSS,
   mobile sizing, z-index/duplicate-instance, and capture-phase click
   hijacking fixes still didn't make its dismiss button work on his end.
   Launcher + panel (open/close, chips, typed Q&A) is the confirmed-
   working part this was rebuilt around -- untouched below.

   NOTE (2026-08-20): every agent bubble (scripted or AI-streamed) now
   reveals via createRevealer() -- a small paced queue that fades each
   chunk in -- instead of writing text straight to the DOM as it became
   available. Per Alex: "whenever a message is sent, the message from
   the AI pops on in large blocks of text ... we want it to come on at
   a readable speed, kind of like Claude Code does or ChatGPT ... fade
   on in a smooth way so that it looks like typing." The "large blocks"
   were real network deltas -- a single SSE chunk from the AI backend
   can be 100+ characters at once, and the old code appended each one
   straight to the DOM the instant it arrived.

   NOTE (2026-08-20): trackViewport() keeps the panel's bottom offset and
   max-height in sync with window.visualViewport, per Alex reporting that
   tapping the composer on his phone "fucks up the viewport ... not
   acting like a normal typing section where it keeps everything in view
   properly." Root cause turned out to be two layered bugs: (1) the
   composer textarea was under iOS Safari's 16px auto-zoom-on-focus
   threshold, so the whole page was zooming in and panning around --
   fixed by bumping it to 16px; (2) underneath that, the ordinary "iOS
   keyboard shrinks the visual viewport but position:fixed/100vh stay
   pinned to the full un-shrunk layout viewport" issue, which trackViewport
   fixes; and (3) even once positioned correctly, the panel was painting
   UNDERNEATH the theme's own site header (.theme__header, z-index 2300).
   After six rounds of class-based CSS fixes produced no visible change on
   his device, the keyboard-open geometry moved to inline styles applied
   from JS (applyKeyboardLayout) on a root reparented to <body>
   (ensureBodyLevel), which cannot lose a specificity, source-order,
   stacking-context, or partial-file-sync race the way a stylesheet rule
   can. The nova-chat--kb-open class still exists purely for the
   header-shrink cosmetics in snippets/lp-nova-chat.liquid.
   ========================================================================== */
(function () {
  'use strict';

  var TYPING_MS = 1400;      // perceived "writing" pause before an answer
  var INTRO_LEAD_MS = 700;   // pause before the founder "starts typing" the intro
  var INTRO_GAP_MS = 450;    // gap between intro messages
  var REVEAL_BATCH = 3;      // characters revealed per reveal tick
  var REVEAL_TICK_MS = 26;   // ms between reveal ticks (~115 chars/sec -- readable typing pace)
  // Only shown if the AI backend is unreachable/unset/erroring before any
  // real text streamed in -- a real reply that's merely slow still
  // arrives via streamReply, this never races a working response.
  var FALLBACK_TEXT = 'Hmm, having trouble connecting right now. Try again in a sec, or email us directly at orders@shopnovaelements.com.';

  function normalize(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // iOS Safari bug: position:fixed elements stay pinned to the LAYOUT
  // viewport (the full page, keyboard-agnostic), while the on-screen
  // keyboard only shrinks the VISUAL viewport -- so the panel's own
  // bottom offset and 100vh-based max-height don't react to the keyboard
  // opening at all. Per Alex, tapping the composer "fucks up the
  // viewport ... not acting like a normal typing section where it keeps
  // everything in view properly" -- what's actually showing through the
  // gap is the PAGE's own fixed elements (its sticky Add to Cart bar),
  // which have the identical bug; ours is the only one this file can fix.
  // visualViewport is the only API that reports the real, keyboard-aware
  // visible size -- kept in sync here as two CSS custom properties the
  // panel's bottom/max-height read from (see snippets/lp-nova-chat.liquid).
  // Keyboard-open layout, applied as INLINE STYLE from JS rather than via a
  // CSS class. Six rounds of class-based CSS fixes for this produced no
  // visible change on Alex's phone ("nothing is fucking fixed here"), and
  // each round could only disprove one hypothesis. Inline styles remove the
  // whole category at once: they beat every stylesheet rule regardless of
  // specificity, source order, media-query nesting, or which file synced
  // to Shopify first. Paired with reparenting the widget to <body> (see
  // ensureBodyLevel) and a maximal z-index, there is no ancestor stacking
  // context, no competing selector, and no theme rule (.theme__header is
  // z-index 2300, cart drawer 6000, tooltips 7000) that can override it.
  //
  // What the debug readout established before this rewrite: the class WAS
  // toggling (kbOpen=true, bottomInset=355.67 on a 766px-tall layout
  // viewport), and the panel's top edge was landing exactly at the site
  // header's bottom edge -- i.e. correctly positioned at top:0 but painted
  // underneath the header. So the geometry below is the same geometry the
  // CSS was already computing; only the delivery mechanism is different.
  var PANEL_KB_STYLE = {
    position: 'fixed',
    left: '0px',
    right: '0px',
    bottom: 'auto',
    width: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    minHeight: '0px',
    borderRadius: '0px',
    zIndex: '2147483647'
  };
  // Every property PANEL_KB_STYLE sets has to be cleared on the way out, or
  // the panel keeps its full-screen geometry after the keyboard closes.
  // top/height are computed per-update, so they're cleared explicitly too.
  var PANEL_KB_KEYS = Object.keys(PANEL_KB_STYLE).concat(['top', 'height']);

  function applyKeyboardLayout(root, panel, chipsWrap, vv) {
    root.style.zIndex = '2147483647';
    for (var k in PANEL_KB_STYLE) {
      if (Object.prototype.hasOwnProperty.call(PANEL_KB_STYLE, k)) {
        panel.style[k] = PANEL_KB_STYLE[k];
      }
    }
    // Fixed elements position against the LAYOUT viewport on iOS, while
    // the visible strip is the VISUAL viewport -- offsetTop is the gap
    // between the two. Anchoring top to it, and giving the panel an
    // explicit height, makes the box exactly cover what the user can see,
    // with no sign errors possible (an earlier `bottom:` formula could go
    // negative once offsetTop grew). 12px keeps the composer just clear
    // of the keys, matching the reference layout Alex sent.
    panel.style.top = vv.offsetTop + 'px';
    panel.style.height = Math.max(160, vv.height - 12) + 'px';
    // The chips row is a big block of vertical space and it duplicates
    // what typing does. The reference layout Alex sent doesn't show
    // suggestions while the keyboard is up either -- give the room to the
    // conversation instead. They come straight back on close.
    if (chipsWrap) chipsWrap.style.display = 'none';
  }

  function clearKeyboardLayout(root, panel, chipsWrap) {
    root.style.zIndex = '';
    PANEL_KB_KEYS.forEach(function (k) { panel.style[k] = ''; });
    if (chipsWrap) chipsWrap.style.display = '';
  }

  // Page scroll lock while the panel is open. Per Alex: "when I try to
  // scroll through the bot, if I don't grab it at the right spot, it
  // scrolls on the actual page behind the bot. When the bot is open, the
  // page itself needs to be locked movement-wise."
  //
  // overscroll-behavior on the thread (see the snippet's CSS) only covers
  // scroll CHAINING -- a flick that starts inside the thread and runs past
  // its end. It does nothing for a touch that starts on the header, the
  // chips, or the composer, since those aren't scroll containers at all,
  // so the gesture goes straight to the page. Locking the body covers
  // both, and is the only approach that actually holds on iOS Safari
  // (`overflow: hidden` on html/body is widely ignored there).
  //
  // Mobile only: on desktop the panel is a small floating card in the
  // corner, and freezing the whole page because a chat window is open
  // would be worse than the problem.
  var pageLock = { active: false, scrollY: 0, prev: null };
  var LOCKED_PROPS = ['position', 'top', 'left', 'right', 'width', 'overflow'];

  function isMobileViewport() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 560px)').matches);
  }

  function lockPage() {
    if (pageLock.active || !isMobileViewport()) return;
    var b = document.body;
    pageLock.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    pageLock.prev = {};
    LOCKED_PROPS.forEach(function (p) { pageLock.prev[p] = b.style[p]; });
    // Pinning the body at a negative top preserves the visual scroll
    // position -- without it the page jumps to the top behind the panel.
    b.style.position = 'fixed';
    b.style.top = (-pageLock.scrollY) + 'px';
    b.style.left = '0px';
    b.style.right = '0px';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
    pageLock.active = true;
  }

  function unlockPage() {
    if (!pageLock.active) return;
    var b = document.body;
    LOCKED_PROPS.forEach(function (p) { b.style[p] = (pageLock.prev && pageLock.prev[p]) || ''; });
    pageLock.active = false;
    // Restore where they actually were, not the top of the page.
    window.scrollTo(0, pageLock.scrollY);
  }

  // position:fixed and z-index are both relative to the nearest ancestor
  // that established a containing block / stacking context (any transform,
  // filter, backdrop-filter, contain, or will-change on a parent does it).
  // Nothing in this theme currently does that above the widget, but a
  // section wrapper, an A/B-testing app, or a page-speed optimizer wrapping
  // the DOM later would silently reintroduce exactly the class of bug this
  // whole saga was -- and it would look identical to a CSS mistake. Being a
  // direct child of <body> makes that structurally impossible.
  function ensureBodyLevel(root) {
    if (root.parentNode !== document.body) document.body.appendChild(root);
  }

  function trackViewport(root) {
    if (!window.visualViewport) return; // no signal -- CSS keeps its static 100vh/0px fallback, current behavior
    var vv = window.visualViewport;
    var panel = root.querySelector('[data-nova-panel]');
    if (!panel) return;
    var chipsWrap = root.querySelector('[data-nova-chips]');
    // Below this, treat the difference as Safari's own toolbar show/hide
    // jitter (usually well under 100px), not a real on-screen keyboard.
    var KEYBOARD_THRESHOLD = 150;
    var applied = false;

    // Kept behind a flag rather than deleted: this is what finally
    // identified both the stacking bug and the offsetTop bug below, and
    // it costs nothing when the flag is absent.
    var debugEl = null;
    if (window.location.search.indexOf('novadebug=1') > -1) {
      debugEl = document.createElement('pre');
      debugEl.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483647;margin:0;' +
        'padding:6px 8px;background:rgba(200,0,0,.92);color:#fff;font:11px/1.4 monospace;' +
        'white-space:pre-wrap;pointer-events:none;';
      document.body.appendChild(debugEl);
    }

    function update() {
      root.style.setProperty('--nova-chat-vvh', vv.height + 'px');
      // Keyboard height is layout-viewport height minus visual-viewport
      // height. offsetTop must NOT be subtracted here: Safari sets it when
      // it scrolls the visual viewport to reveal a focused input (exactly
      // what tapping this composer does), and subtracting it cancelled the
      // measurement out to ~0, so the widget concluded there was no
      // keyboard and skipped this whole layout. That's why it worked when
      // vv.offsetTop happened to be 0 and silently did nothing otherwise.
      var keyboardHeight = Math.max(0, window.innerHeight - vv.height);
      root.style.setProperty('--nova-chat-vv-bottom', keyboardHeight + 'px');

      var kbOpen = keyboardHeight > KEYBOARD_THRESHOLD;
      root.classList.toggle('nova-chat--kb-open', kbOpen); // kept for the header-shrink rules in the snippet's CSS
      if (kbOpen) {
        ensureBodyLevel(root);
        applyKeyboardLayout(root, panel, chipsWrap, vv);
        applied = true;
      } else if (applied) {
        clearKeyboardLayout(root, panel, chipsWrap);
        applied = false;
      }
      if (debugEl) {
        debugEl.textContent =
          'innerH=' + window.innerHeight + ' vv.h=' + Math.round(vv.height) +
          ' vv.top=' + Math.round(vv.offsetTop) + ' kbH=' + Math.round(keyboardHeight) +
          ' kbOpen=' + kbOpen;
      }
    }
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
  }

  // Ported from assets/nova-chat-landing.js on ai-chat-pdp (same
  // sessionStorage key pattern, own key name). Persists for the tab's
  // session so a reload mid-conversation keeps the same backend session
  // (rate limiting + the future 1-discount-per-session guarantee are
  // keyed off this).
  function getSessionId() {
    var KEY = 'nova-chat-session-id';
    try {
      var existing = window.sessionStorage.getItem(KEY);
      if (existing) return existing;
      var id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : (Date.now() + '-' + Math.random().toString(16).slice(2));
      window.sessionStorage.setItem(KEY, id);
      return id;
    } catch (e) {
      return Date.now() + '-' + Math.random().toString(16).slice(2);
    }
  }

  function init(root) {
    if (root.dataset.novaChatReady) return;
    root.dataset.novaChatReady = '1';

    var panel     = root.querySelector('[data-nova-panel]');
    var thread    = root.querySelector('[data-nova-thread]');
    var chipsWrap = root.querySelector('[data-nova-chips]');
    var form      = root.querySelector('[data-nova-form]');
    var input     = root.querySelector('[data-nova-input]');
    var sendBtn   = root.querySelector('[data-nova-send]');
    var launcher  = root.querySelector('[data-nova-toggle]');
    var badge     = root.querySelector('[data-nova-badge]');
    var agentName = root.dataset.agentName || 'Alex';

    var content = { intro: [], faq: [], chatEndpoint: '', variants: [] };
    var raw = root.querySelector('[data-nova-chat-content]');
    if (raw) { try { content = JSON.parse(raw.textContent); } catch (e) {} }
    var intro = (content.intro || []).filter(function (t) { return t && t.trim(); });
    var faq = (content.faq || []).filter(function (f) { return f && f.question && f.answer; });
    var chatEndpoint = content.chatEndpoint || '';
    var labelImageUrl = content.labelImageUrl || '';
    var variants = content.variants || [];
    // Same Worker, sibling route -- derived rather than configured
    // separately so there's no way for the two to drift to different
    // deployments.
    var eventEndpoint = chatEndpoint ? chatEndpoint.replace(/\/chat$/, '/event') : '';

    var open = false;
    var askedOnce = false;
    var introPlayed = false;
    var sessionId = getSessionId();

    // Fire-and-forget funnel beacon. Never awaited, never surfaces an
    // error: analytics failing must not affect the conversation. keepalive
    // lets a beacon sent as the visitor leaves still complete.
    function track(name, detail) {
      if (!eventEndpoint) return;
      try {
        fetch(eventEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sessionId, name: name, detail: detail || undefined }),
          keepalive: true
        }).catch(function () {});
      } catch (e) { /* never let tracking break the widget */ }
    }
    var chatHistory = []; // {role: 'user'|'assistant', content: string}[] -- sent whole each request, backend is stateless

    /* --- thread ---------------------------------------------------------- */
    function scrollThread() {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { thread.scrollTop = thread.scrollHeight - thread.clientHeight; });
      });
    }

    function bubble(side, html, meta) {
      var wrap = document.createElement('div');
      wrap.className = 'nova-chat__msg nova-chat__msg--' + side;
      var p = document.createElement('p');
      p.className = 'nova-chat__bubble';
      p.innerHTML = html;                      // only ever snippet-authored copy
      wrap.appendChild(p);
      if (meta) {
        var m = document.createElement('p');
        m.className = 'nova-chat__meta';
        m.textContent = meta;
        wrap.appendChild(m);
      }
      thread.appendChild(wrap);
      scrollThread();
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function showTyping() {
      var t = document.createElement('div');
      t.className = 'nova-chat__typing';
      t.setAttribute('data-nova-typing', '');
      t.innerHTML = '<i></i><i></i><i></i>';
      thread.appendChild(t);
      scrollThread();
    }
    function clearTyping() {
      var t = thread.querySelector('[data-nova-typing]');
      if (t) t.remove();
    }

    var SUPPORT_EMAIL = 'orders@shopnovaelements.com';
    var SUPPORT_MAILTO = 'mailto:' + SUPPORT_EMAIL + '?subject=' + encodeURIComponent('Nova Prime question');

    // Turns the support address, wherever it sits in a bubble's text,
    // into a tappable mailto (Alex's friend: "make it so they can tap
    // somewhere to start an email in their default mail app"). Walks
    // text nodes, so it covers the revealer's spans and plain text
    // alike; the address never splits across spans because REVEAL_BATCH
    // chunks can cut mid-word, so this runs only once a bubble is
    // complete, on the joined text of each span.
    function linkifySupportEmail(root) {
      if (!root) return;
      // Re-join the reveal spans first: a chunk boundary can fall inside
      // the address, which would hide it from a per-node search.
      var spans = root.querySelectorAll('.nova-chat__reveal');
      if (spans.length > 1) {
        var joined = document.createElement('span');
        joined.className = 'nova-chat__reveal is-in';
        joined.textContent = Array.prototype.map.call(spans, function (el) { return el.textContent; }).join('');
        root.insertBefore(joined, spans[0]);
        Array.prototype.forEach.call(spans, function (el) { el.remove(); });
      }
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      var hits = [];
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.indexOf(SUPPORT_EMAIL) !== -1 && !node.parentNode.closest('a')) hits.push(node);
      }
      hits.forEach(function (textNode) {
        var parts = textNode.nodeValue.split(SUPPORT_EMAIL);
        var frag = document.createDocumentFragment();
        parts.forEach(function (part, i) {
          if (part) frag.appendChild(document.createTextNode(part));
          if (i < parts.length - 1) {
            var a = document.createElement('a');
            a.className = 'nova-chat__mail';
            a.href = SUPPORT_MAILTO;
            a.textContent = SUPPORT_EMAIL;
            frag.appendChild(a);
          }
        });
        textNode.parentNode.replaceChild(frag, textNode);
      });
    }

    // Empty bubble to reveal text into progressively. Returns the
    // <p class="nova-chat__bubble"> itself.
    function startAgentBubble() {
      var wrap = document.createElement('div');
      wrap.className = 'nova-chat__msg nova-chat__msg--agent';
      var p = document.createElement('p');
      p.className = 'nova-chat__bubble';
      wrap.appendChild(p);
      var m = document.createElement('p');
      m.className = 'nova-chat__meta';
      m.textContent = agentName;
      wrap.appendChild(m);
      thread.appendChild(wrap);
      scrollThread();
      return p;
    }

    // Decouples "when text arrives" from "when it's shown" -- per Alex:
    // "we don't want them to pop on. We want them to kind of fade on in
    // a smooth way so that it looks like typing... kind of like Claude
    // Code does or ChatGPT." A single network delta can be 100+
    // characters at once (Anthropic's own token batching, plus whatever
    // Cloudflare's transport coalesces) -- appending deltas straight to
    // the DOM as they arrived was exactly the "pops on in large blocks"
    // Alex was describing. Feed text in via push() as it becomes
    // available (all at once for scripted copy, incrementally for the
    // real AI stream); this reveals it at a steady pace regardless of
    // how it arrived, each small chunk fading in via the
    // .nova-chat__reveal CSS transition. finish() marks no more input
    // is coming; onDone fires once revealed has actually caught up to
    // it, not before -- callers that need to act only after the bubble
    // visually finishes (push to history, show a widget) wait on that,
    // not on the network finishing.
    function createRevealer(p) {
      var queue = '';
      var timer = null;
      var inputDone = false;
      var doneCb = null;
      var instant = prefersReducedMotion();

      function maybeFinish() {
        if (inputDone && !queue.length && doneCb) {
          var cb = doneCb;
          doneCb = null;
          cb();
        }
      }

      function tick() {
        if (!queue.length) { timer = null; maybeFinish(); return; }
        var take = Math.min(REVEAL_BATCH, queue.length);
        var chunk = queue.slice(0, take);
        queue = queue.slice(take);
        var span = document.createElement('span');
        span.className = 'nova-chat__reveal';
        span.textContent = chunk;
        p.appendChild(span);
        void span.offsetWidth; // force layout so the fade-in transition actually plays, not skipped
        span.classList.add('is-in');
        scrollThread();
        timer = window.setTimeout(tick, REVEAL_TICK_MS);
      }

      return {
        push: function (text) {
          if (!text) return;
          // Backstop for the house rule the Worker already enforces: no em
          // dash reaches the bubble; a stray "word ," from a dash at a
          // delta edge is closed up.
          text = text.replace(/\s*—\s*/g, ', ').replace(/ +,/g, ',');
          if (instant) { p.textContent += text; scrollThread(); return; }
          queue += text;
          if (!timer) tick();
        },
        finish: function () {
          inputDone = true;
          maybeFinish();
        },
        onDone: function (fn) { doneCb = fn; }
      };
    }

    /* --- agent bubbles: typing pause, then a smooth paced reveal --------- */
    function addAgentBubble(text, opts) {
      opts = opts || {};
      return new Promise(function (resolve) {
        showTyping();
        setTimeout(function () {
          clearTyping();
          var p = startAgentBubble();
          var revealer = createRevealer(p);
          revealer.onDone(function () {
            linkifySupportEmail(p);
            if (opts.linkUrl) {
              p.appendChild(document.createTextNode(' '));
              var a = document.createElement('a');
              a.href = opts.linkUrl;
              a.textContent = opts.linkLabel || 'See details';
              p.appendChild(a);
              scrollThread();
            }
            resolve();
          });
          revealer.push(text);
          revealer.finish();
        }, TYPING_MS);
      });
    }

    /* --- scripted intro: plays once, right after the greeting, before
       any FAQ chips show ---------------------------------------------- */
    function playIntro() {
      if (introPlayed || !intro.length) {
        renderChips();
        return;
      }
      introPlayed = true;
      var chain = Promise.resolve();
      intro.forEach(function (msg, i) {
        chain = chain.then(function () {
          return new Promise(function (resolve) {
            setTimeout(function () {
              addAgentBubble(msg).then(resolve);
            }, i === 0 ? INTRO_LEAD_MS : INTRO_GAP_MS);
          });
        });
      });
      chain.then(function () {
        track('intro_completed');
        renderChips();
      });
    }

    /* --- answers --------------------------------------------------------- */
    function findAnswer(text) {
      var q = normalize(text);
      if (!q) return null;
      for (var i = 0; i < faq.length; i++) {
        if (normalize(faq[i].question) === q) return faq[i];
      }
      // loose match: every significant word of the FAQ question appears
      for (var j = 0; j < faq.length; j++) {
        var words = normalize(faq[j].question).split(' ').filter(function (w) { return w.length > 3; });
        if (words.length && words.every(function (w) { return q.indexOf(w) > -1; })) return faq[j];
      }
      return null;
    }

    // Catches real conversational phrasing ("can you add it to my cart",
    // "I'll take the mango one") that findAnswer's exact/loose match on
    // the ONE curated "Which flavor should I get?" question misses. Real
    // gap found live: anything not matching that exact question fell
    // straight to the AI, which has zero knowledge this widget exists
    // and just told people to use the page's own Add to Cart button
    // instead -- per Alex, "the chat keeps trying to direct me to add it
    // to my cart myself." This intercepts flavor/cart intent BEFORE it
    // ever reaches the AI, so phrasing doesn't matter.
    var FLAVOR_INTENT_WORDS = [
      'flavor', 'flavour', 'pina colada', 'piña colada', 'mango',
      'add to cart', 'add it to my cart', 'add this to my cart',
      'add to my cart', 'buy it', 'buy this', 'buy one', 'purchase this',
      'checkout', 'i want to buy', "i'll take", 'order this'
    ];
    function wantsFlavorPicker(text) {
      var q = normalize(text);
      if (!q || !variants.length) return false;
      return FLAVOR_INTENT_WORDS.some(function (w) { return q.indexOf(normalize(w)) > -1; });
    }

    // Real add-to-cart, routed through this theme's own pipeline
    // (theme.dev.js's <cart-items>/CartItems, same one
    // snippets/product-buttons.liquid uses) instead of a hand-rolled
    // fetch to /cart/add.js. Builds a real, if invisible, <form> with
    // the id/quantity fields CartItems.cartAddEvent expects on the
    // dispatched button, and dispatches the theme's own
    // `theme:cart:add` CustomEvent -- cart refresh, the header's
    // <cart-count>, and the cart drawer auto-opening on success are
    // all the theme's existing behavior, not rebuilt here.
    function addVariantToCart(variantId, qty) {
      return new Promise(function (resolve, reject) {
        var cartForm = document.createElement('form');
        cartForm.style.display = 'none';
        var idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.value = variantId;
        var qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        qtyInput.value = String(qty || 1);
        var submitBtn = document.createElement('button');
        submitBtn.type = 'submit';
        cartForm.appendChild(idInput);
        cartForm.appendChild(qtyInput);
        cartForm.appendChild(submitBtn);
        document.body.appendChild(cartForm);

        var done = false;
        function cleanup() {
          document.removeEventListener('theme:product:added', onSuccess);
          document.removeEventListener('theme:product:add-error', onError);
          document.removeEventListener('theme:cart:error', onError);
          cartForm.remove();
        }
        function onSuccess() {
          if (done) return;
          done = true;
          cleanup();
          resolve();
        }
        function onError() {
          if (done) return;
          done = true;
          cleanup();
          reject();
        }
        document.addEventListener('theme:product:added', onSuccess);
        document.addEventListener('theme:product:add-error', onError);
        document.addEventListener('theme:cart:error', onError);
        window.setTimeout(onError, 6000); // safety net if neither event ever fires

        document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { button: submitBtn } }));
      });
    }

    // The consolidated purchase card: flavor, then a one-click upsell,
    // then straight to cart -- all inside this one widget, no further
    // chat turns needed to complete it. Per Alex: "It should never be
    // like, 'Ready to buy it? Do you want one or two things?' ... all
    // that stuff should be baked into the widget ... so it's not having
    // to take multiple turns of the AI writing messages." Real variants
    // only (from Liquid's product.variants) -- empty on any page
    // without a product, so this quietly renders nothing rather than a
    // picker for products that don't exist.
    function renderPurchaseWidget() {
      if (!variants.length) return;
      var wrap = document.createElement('div');
      wrap.className = 'nova-chat__purchase';
      thread.appendChild(wrap);
      showFlavorStep();
      scrollThread();

      function showFlavorStep() {
        wrap.innerHTML = '';
        var label = document.createElement('p');
        label.className = 'nova-chat__purchase-label';
        label.textContent = 'Pick your flavor:';
        wrap.appendChild(label);
        var row = document.createElement('div');
        row.className = 'nova-chat__flavors';
        variants.forEach(function (v) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nova-chat__flavor';
          btn.disabled = !v.available;
          if (v.image) {
            var img = document.createElement('img');
            img.src = v.image;
            img.alt = '';
            btn.appendChild(img);
          }
          var span = document.createElement('span');
          span.textContent = v.available ? v.title : v.title + ' (sold out)';
          btn.appendChild(span);
          btn.addEventListener('click', function () {
            if (btn.disabled) return;
            track('flavor_selected', v.title);
            showUpsellStep(v);
          });
          row.appendChild(btn);
        });
        wrap.appendChild(row);
        scrollThread();
      }

      function showUpsellStep(chosen) {
        wrap.innerHTML = '';
        var label = document.createElement('p');
        label.className = 'nova-chat__purchase-label';
        label.textContent = chosen.title + ': most people grab a 2nd jar at the bundle price:';
        wrap.appendChild(label);
        var row = document.createElement('div');
        row.className = 'nova-chat__upsell-row';

        var oneBtn = document.createElement('button');
        oneBtn.type = 'button';
        oneBtn.className = 'nova-chat__upsell-btn';
        oneBtn.textContent = 'Just this one';
        oneBtn.addEventListener('click', function () {
          track('upsell_choice', 'declined:1');
          commit(chosen, 1);
        });

        var twoBtn = document.createElement('button');
        twoBtn.type = 'button';
        twoBtn.className = 'nova-chat__upsell-btn nova-chat__upsell-btn--primary';
        twoBtn.textContent = 'Add a 2nd jar';
        twoBtn.addEventListener('click', function () {
          track('upsell_choice', 'accepted:2');
          commit(chosen, 2);
        });

        row.appendChild(oneBtn);
        row.appendChild(twoBtn);
        wrap.appendChild(row);
        scrollThread();
      }

      function commit(chosen, qty) {
        wrap.innerHTML = '';
        var status = document.createElement('p');
        status.className = 'nova-chat__purchase-label';
        status.textContent = 'Adding to your cart…';
        wrap.appendChild(status);
        scrollThread();
        addVariantToCart(chosen.id, qty)
          .then(function () {
            track('add_to_cart_success', qty + '× ' + chosen.title);
            wrap.remove();
            var msg = 'Added ' + qty + ' × ' + chosen.title + ' to your cart!';
            addAgentBubble(msg).then(function () {
              chatHistory.push({ role: 'assistant', content: msg });
            });
          })
          .catch(function () {
            track('add_to_cart_failed', qty + '× ' + chosen.title);
            wrap.remove();
            var msg = 'Hmm, that didn’t go through. Mind trying the Add to Cart button on the page instead?';
            addAgentBubble(msg).then(function () {
              chatHistory.push({ role: 'assistant', content: msg });
            });
          });
      }
    }

    // Tool name -> widget renderer. The backend's tools.js defines what
    // the model can call; this is the ONLY place that has to change on
    // the frontend to support a new one -- add the tool there, teach
    // systemPrompt.js when to use it, add a case here. The streaming
    // relay itself (see streamReply below) never needs to change.
    var WIDGET_HANDLERS = {
      present_purchase_widget: renderPurchaseWidget,
      show_nutrition_label: renderNutritionLabel
    };

    // The Supplement Facts label, dropped into the thread under the reply
    // so the visitor reads the panel themselves (Alex: any nutrition
    // answer should come with the label). The image URL is resolved in
    // lp-nova-chat.liquid; with none there is nothing to show.
    function renderNutritionLabel() {
      if (!labelImageUrl) return;
      var wrap = document.createElement('div');
      wrap.className = 'nova-chat__label';
      var img = document.createElement('img');
      img.src = labelImageUrl;
      img.alt = 'Nova Prime Blend Supplement Facts label';
      img.loading = 'lazy';
      wrap.appendChild(img);
      thread.appendChild(wrap);
      scrollThread();
    }

    // Curated FAQ entries (section blocks -- free, instant, exact/loose-
    // matched) answer first when one exists. Everything else falls
    // through to the real AI -- this is the hybrid: a chip tap always
    // hits its own exact FAQ entry and never costs an API call; a real
    // typed question that isn't covered gets a real answer instead of
    // the old "I don't have that" dead end.
    function answer(text) {
      var hit = findAnswer(text);
      if (hit) {
        track('faq_answered', hit.question);
        addAgentBubble(hit.answer, { linkUrl: hit.linkUrl, linkLabel: hit.linkLabel }).then(function () {
          chatHistory.push({ role: 'assistant', content: hit.answer });
          if (hit.showFlavorPicker) renderPurchaseWidget();
        });
        return;
      }
      if (wantsFlavorPicker(text)) {
        var flavorFaq = faq.filter(function (f) { return f.showFlavorPicker; })[0];
        var flavorText = flavorFaq ? flavorFaq.answer : 'Here are the flavors. Pick one and I’ll get it added to your cart.';
        addAgentBubble(flavorText).then(function () {
          chatHistory.push({ role: 'assistant', content: flavorText });
          renderPurchaseWidget();
        });
        return;
      }
      streamReply();
    }

    // Real backend call -- POST /chat on the nova-chat-agent Worker
    // (backend/ on ai-chat-pdp), streamed token-by-token into a bubble.
    // Ported from nova-chat-landing.js's streamReply/pump, same SSE
    // `data: {"delta":"..."}` contract and the same "leave a partial
    // reply alone rather than stack a second, contradictory message on
    // failure" rule. orderContext intentionally omitted -- this widget
    // doesn't gather live cart/variant data; the backend's system
    // prompt already degrades gracefully without it (won't state a
    // specific price it wasn't given).
    function streamReply() {
      if (!chatEndpoint) {
        clearTyping();
        bubble('agent', escapeHtml(FALLBACK_TEXT), agentName);
        return;
      }
      showTyping();
      var row = null;
      var revealer = null;
      var fullText = '';
      var pendingWidget = null;
      var finalized = false;

      // Runs once the bubble has actually finished revealing on screen
      // (or immediately for a widget-only turn with no text at all) --
      // never on network completion alone, so a widget never appears
      // mid-typewriter, before the text it followed has finished coming in.
      function finalize() {
        if (finalized) return;
        finalized = true;
        linkifySupportEmail(row); // the bubble has fully revealed; make the address tappable
        // A widget-only turn (no accompanying text) still needs a
        // non-empty assistant entry -- ChatRequestSchema requires
        // content.min(1) on every future request this history is sent
        // back in.
        chatHistory.push({ role: 'assistant', content: fullText || ('[Showed widget: ' + pendingWidget + ']') });
        if (pendingWidget) {
          var handler = WIDGET_HANDLERS[pendingWidget];
          if (handler) handler();
        }
      }

      fetch(chatEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          messages: chatHistory.slice(-40), // ChatRequestSchema caps at 40
          // How many were trimmed off the front, so the backend can log
          // each message at its true conversation position instead of its
          // index in this payload (see schema.sql on transcript dedup).
          historyOffset: Math.max(0, chatHistory.length - 40)
        })
      })
        .then(function (res) {
          if (!res.ok || !res.body) throw new Error('chat_http_' + res.status);
          var reader = res.body.getReader();
          var decoder = new TextDecoder();
          var buffer = '';

          function pump() {
            return reader.read().then(function (result) {
              if (result.done) {
                if (!row && !pendingWidget) throw new Error('chat_empty_reply');
                clearTyping(); // covers a tool-only turn: dots never got cleared by a text delta
                if (revealer) {
                  revealer.finish(); // reveal may still be catching up -- finalize() fires from onDone, not here
                } else {
                  finalize();
                }
                return;
              }
              buffer += decoder.decode(result.value, { stream: true });
              var lines = buffer.split('\n');
              buffer = lines.pop() || '';
              lines.forEach(function (line) {
                if (line.indexOf('data: ') !== 0) return;
                try {
                  var evt = JSON.parse(line.slice(6));
                  if (evt.delta) {
                    if (!row) {
                      clearTyping();
                      row = startAgentBubble();
                      revealer = createRevealer(row);
                      revealer.onDone(finalize);
                    }
                    fullText += evt.delta;
                    revealer.push(evt.delta);
                  } else if (evt.widget) {
                    pendingWidget = evt.widget;
                  }
                } catch (e) { /* skip partial/malformed SSE line */ }
              });
              return pump();
            });
          }

          return pump();
        })
        .catch(function () {
          clearTyping();
          // A partial reply already streamed in before the failure --
          // leave it, better than stacking a second, contradictory bot
          // message under a real (if incomplete) one.
          if (row) return;
          bubble('agent', escapeHtml(FALLBACK_TEXT), agentName);
        });
    }

    function submit(text) {
      var body = (text || '').trim();
      if (!body) return;
      bubble('user', escapeHtml(body), 'Delivered');
      chatHistory.push({ role: 'user', content: body });
      input.value = '';
      syncSend();
      if (!askedOnce) { askedOnce = true; chipsWrap.innerHTML = ''; }
      answer(body);
    }

    /* --- chips ----------------------------------------------------------- */
    function renderChips() {
      chipsWrap.innerHTML = '';
      faq.slice(0, 4).forEach(function (entry) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nova-chat__chip';
        b.textContent = entry.question;
        b.addEventListener('click', function () {
          track('chip_clicked', entry.question);
          submit(entry.question);
        });
        chipsWrap.appendChild(b);
      });
    }

    /* --- open / close ---------------------------------------------------- */
    function setOpen(next) {
      // Only a real transition is worth a beacon -- init calls setOpen(false)
      // on every page load, which would otherwise log a chat_closed for
      // every visitor who never touched the widget.
      var changed = open !== next;
      open = next;
      panel.hidden = !open;
      launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
      launcher.setAttribute('aria-label', open ? 'Close chat' : 'Open chat');
      if (badge) badge.hidden = open;
      if (changed) track(open ? 'chat_opened' : 'chat_closed');
      if (open) {
        lockPage();
        if (!introPlayed) {
          playIntro();
        } else if (!chipsWrap.children.length && !askedOnce) {
          renderChips();
        }
        scrollThread();
        setTimeout(function () { input.focus(); }, 60);
      } else {
        unlockPage();
      }
    }

    function syncSend() {
      sendBtn.disabled = !input.value.trim();
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 96) + 'px';
    }

    /* --- wiring -----------------------------------------------------------
       Every click handler below runs in the CAPTURE phase and hard-stops
       the event (preventDefault + stopImmediatePropagation) before it can
       reach any other script's listener. This store runs an A/B-testing
       app and a page-speed optimizer, both categories known for attaching
       their own document-level click listeners. ------------------------- */
    function claim(el, handler) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handler(e);
      }, true);
    }

    claim(launcher, function () { setOpen(!open); });
    root.querySelectorAll('[data-nova-close]').forEach(function (el) {
      claim(el, function () { setOpen(false); });
    });

    input.addEventListener('input', syncSend);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(input.value); }
    });
    form.addEventListener('submit', function (e) { e.preventDefault(); submit(input.value); });

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && open) setOpen(false); });

    trackViewport(root);
    setOpen(false);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-nova-chat]');
    if (roots.length > 1) {
      // A static {% section %} tag can get re-rendered by the theme
      // editor without the previous copy being removed from the DOM.
      // Keep only the newest instance.
      for (var i = 0; i < roots.length - 1; i++) {
        roots[i].parentNode.removeChild(roots[i]);
      }
      roots = document.querySelectorAll('[data-nova-chat]');
    }
    roots.forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
